/**
 * Governance Rules — Static Data
 *
 * All validation rules, rulebook definitions, pipeline file references,
 * and stage/severity configuration for the GovernanceRulesTab.
 *
 * Canonical location: portals/admin/content/governance/ (DDD — UI in portal)
 */

import { DEFAULT_POLICY } from '../../../../governance'

// ─── Types ────────────────────────────────────────────────────

export type Severity = 'BLOCKER' | 'WARNING' | 'INFO'

export type Stage =
  | 'schema'
  | 'type'
  | 'content'
  | 'pedagogy'
  | 'support'
  | 'visual'
  | 'graph'
  | 'seed'
  | 'policy'

export interface Rule {
  id: string
  label: string
  description: string
  severity: Severity
  stage: Stage
  category: string
  example?: string
  fix?: string
}

export type ViewId = 'regels' | 'regelboeken' | 'drempelwaarden'

export interface RulebookFile {
  id: string
  name: string
  path: string
  description: string
  module: string
  color: string
  keyRules: string[]
}

export interface PipelineFile {
  name: string
  path: string
  stage: Stage
  description: string
}

// ─── Stage Config ─────────────────────────────────────────────

export const STAGE_LABELS: Record<Stage, string> = {
  schema: 'Schema',
  type: 'Vraagtype',
  content: 'Inhoud',
  pedagogy: 'Pedagogie',
  support: 'Ondersteuning',
  visual: 'Visueel',
  graph: 'LDG-Graaf',
  seed: 'Seed-integriteit',
  policy: 'Beleid',
}

export const STAGE_COLORS: Record<Stage, string> = {
  schema: 'bg-slate-100 text-slate-700',
  type: 'bg-blue-100 text-blue-700',
  content: 'bg-violet-100 text-violet-700',
  pedagogy: 'bg-purple-100 text-purple-700',
  support: 'bg-teal-100 text-teal-700',
  visual: 'bg-orange-100 text-orange-700',
  graph: 'bg-pink-100 text-pink-700',
  seed: 'bg-lime-100 text-lime-700',
  policy: 'bg-amber-100 text-amber-700',
}

export const ALL_STAGES: (Stage | 'all')[] = [
  'all',
  'schema',
  'type',
  'content',
  'pedagogy',
  'support',
  'visual',
  'graph',
  'seed',
  'policy',
]

// ─── Validation Rules ─────────────────────────────────────────

export const RULES: Rule[] = [
  // Schema Stage
  {
    id: 'SCH-001',
    label: 'Verplichte velden aanwezig',
    description:
      'Elke vraag moet de velden id, familyId, module, skill, subSkill, tier, levelCode, group, question, options, correctAnswer en feedback bevatten.',
    severity: 'BLOCKER',
    stage: 'schema',
    category: 'Structuur',
    example: 'Zie: src/governance/pipeline/stages/schemaStage.ts',
    fix: 'Voeg de ontbrekende verplichte velden toe aan de vraagdefinitie.',
  },
  {
    id: 'SCH-002',
    label: 'Uniek vraag-ID',
    description: 'Elk vraag-ID moet uniek zijn binnen het volledige systeem.',
    severity: 'BLOCKER',
    stage: 'schema',
    category: 'Structuur',
    fix: 'Gebruik het naamgevingsschema: [FAMILIE]-[NUMMER] (bijv. GET-BREUK-BASIS-001).',
  },
  {
    id: 'SCH-003',
    label: 'Geldige tier (1–16)',
    description: 'Tier moet een getal zijn tussen 1 en 16.',
    severity: 'BLOCKER',
    stage: 'schema',
    category: 'Metadata',
    fix: 'Pas de tier aan naar een waarde tussen 1 en 16.',
  },
  {
    id: 'SCH-004',
    label: 'Geldig groepsjaar (6, 7 of 8)',
    description: 'Het veld "group" moet 6, 7 of 8 zijn.',
    severity: 'BLOCKER',
    stage: 'schema',
    category: 'Metadata',
    fix: 'Stel group in op 6, 7 of 8.',
  },
  // Type Stage
  {
    id: 'TYP-001',
    label: 'Geldig vraagtype',
    description:
      'Het vraagtype moet geregistreerd zijn in het vraagtype-register (questionTypeRegistry.ts). Er zijn 29 geregistreerde vraagtypes.',
    severity: 'BLOCKER',
    stage: 'type',
    category: 'Vraagtype',
    example:
      'Geldige typen: multiple_choice, fill_in, ordering, matching, drag_drop_fill, table_reading, number_sequence, ratio_table, text_highlight, bar_chart, line_chart, pie_chart, schedule_reading, cijferend_optellen, cijferend_aftrekken, cijferend_vermenigvuldigen, cijferend_delen, clock, money, multiplication_grid, true_false_series, estimation_slider, categorize, error_spotting, comparison_judge, sentence_error_judge, sentence_parser, step_solver, verb_conjugation_flow',
    fix: 'Gebruik een van de geregistreerde vraagtypes.',
  },
  {
    id: 'TYP-002',
    label: 'Antwoordformaat klopt bij type',
    description:
      'Het antwoordobject moet overeenkomen met het verwachte formaat voor het vraagtype.',
    severity: 'BLOCKER',
    stage: 'type',
    category: 'Vraagtype',
    fix: 'Raadpleeg de vraagtype-specificatie voor het juiste antwoordformaat.',
  },
  {
    id: 'TYP-003',
    label: 'Multiple choice: 2–5 opties',
    description:
      'Meerkeuze-vragen moeten minimaal 2 en maximaal 5 antwoordopties bevatten.',
    severity: 'WARNING',
    stage: 'type',
    category: 'Vraagtype',
    fix: 'Voeg meer antwoordopties toe of verander het vraagtype.',
  },
  // Content Stage
  {
    id: 'CON-001',
    label: 'Vraagstelling niet leeg',
    description:
      'De vraagstelling mag niet leeg zijn en moet minimaal 10 tekens bevatten.',
    severity: 'BLOCKER',
    stage: 'content',
    category: 'Inhoud',
    fix: 'Voeg een duidelijke vraagstelling toe.',
  },
  {
    id: 'CON-002',
    label: 'Uitleg aanwezig (explanationText)',
    description:
      'feedback.explanationText is altijd verplicht. Minimale lengte afhankelijk van tier-band.',
    severity: 'BLOCKER',
    stage: 'content',
    category: 'Feedback',
    example:
      'Minimale lengte: basic=30, intermediate=50, advanced=80, expert=100 tekens',
    fix: 'Voeg een stap-voor-stap uitleg toe in feedback.explanationText.',
  },
  {
    id: 'CON-003',
    label: 'Distractor-uitleg voor alle opties',
    description:
      'feedback.distractorExplanations moet een uitleg bevatten voor elke antwoordoptie.',
    severity: 'BLOCKER',
    stage: 'content',
    category: 'Feedback',
    fix: 'Voeg een uitleg toe voor elke optie (index 0, 1, 2, 3).',
  },
  {
    id: 'CON-004',
    label: 'Geen verboden vocabulaire voor tier-band',
    description:
      'Vragen op basic-niveau mogen geen technische termen bevatten zoals "notatie", "commutatief", "proportioneel".',
    severity: 'WARNING',
    stage: 'content',
    category: 'Taalgebruik',
    fix: 'Herformuleer de vraag met eenvoudiger taalgebruik.',
  },
  {
    id: 'CON-005',
    label: 'Vraaglengte binnen budget',
    description: 'De vraagstelling mag niet te lang zijn voor het tier-niveau.',
    severity: 'WARNING',
    stage: 'content',
    category: 'Taalgebruik',
    example:
      'Max: basic=200, intermediate=400, advanced=600, expert=800 tekens',
    fix: 'Verkort de vraagstelling.',
  },
  {
    id: 'CON-006',
    label: 'Numerieke schaal-mismatch distractors',
    description:
      'Bij numerieke meerkeuze-vragen moeten distractors in hetzelfde bereik liggen als het correcte antwoord. Kinderen kunnen anders op grootte alleen al gokken (bijv. antwoord=18, distractors allemaal ~6).',
    severity: 'WARNING',
    stage: 'content',
    category: 'Distractors',
    example:
      'Fout: antwoord €60, distractors €6, €5,50, €6,50. Goed: antwoord €60, distractors €57,60, €38,40, €68.',
    fix: 'Maak distractors die plausibele rekenfouten vertegenwoordigen op dezelfde schaal als het correcte antwoord.',
  },
  {
    id: 'CON-007',
    label: 'Visueel onmogelijke spelling-distractors',
    description:
      'Bij taalvragen mogen distractors geen visueel onmogelijke patronen bevatten die kinderen direct kunnen uitsluiten: gemengde hoofdletters (AmsterdaM), driedubbele d/t-clusters (hooftdt), of onmogelijke apostrofposities.',
    severity: 'WARNING',
    stage: 'content',
    category: 'Distractors',
    example:
      'Fout: "AmsterdaM", "hooftdt", "mond\'t". Goed: "Amsterdan", "hooft", "mondt".',
    fix: 'Gebruik distractors die echte spelfouten of misconcepties vertegenwoordigen — geen onmogelijke lettercombinaties.',
  },
  {
    id: 'CON-008',
    label: 'Correct antwoord valt op tussen distractors',
    description:
      'Het correcte antwoord mag niet visueel opvallen tussen de distractors. Detecteert: lengte-outlier (correct veel korter/langer), numerieke spreiding (alle distractors aan dezelfde kant), format-verschil (correct heeft ander format), en distractor-clustering (distractors lijken sterk op elkaar maar niet op correct).',
    severity: 'WARNING',
    stage: 'content',
    category: 'Distractors',
    example:
      'Fout: correct="€57,60" (7 tekens), distractors="Dat kan niet berekend worden" (28 tekens), "Het hangt af van de winkel" (25 tekens), "Onbekend" (8 tekens). Goed: alle opties in vergelijkbaar format en lengte.',
    fix: 'Zorg dat het correcte antwoord qua lengte, format, numeriek bereik en visuele gelijkenis opgaat in de distractors. Varieer distractors zodat ze niet allemaal op elkaar lijken.',
  },
  // Pedagogy Stage (3b)
  {
    id: 'PED-001',
    label: 'DOK-tier afstemming',
    description:
      'Het DOK-niveau (Depth of Knowledge) van de vraag moet passen bij de tier-band. Expert-tiers (13-16) vereisen minimaal DOK 3, advanced (9-12) minimaal DOK 2.',
    severity: 'WARNING',
    stage: 'pedagogy',
    category: 'Pedagogisch Contract',
    example: 'Tier 14 (expert) met DOK 1 (recall) → te laag',
    fix: 'Verhoog de cognitieve complexiteit van de vraag of pas de tier aan.',
  },
  {
    id: 'PED-002',
    label: 'Distractor misconceptie-tagging',
    description:
      'MC-distractors moeten getagd zijn met misconceptie-IDs uit het misconceptie-register. Verplicht voor tier ≥ 9.',
    severity: 'WARNING',
    stage: 'pedagogy',
    category: 'Pedagogisch Contract',
    fix: 'Voeg misconceptionTags toe aan het pedagogyContract van de vraag.',
  },
  {
    id: 'PED-003',
    label: 'Indicator-antwoord pariteit',
    description:
      'Visual indicator-waarden (indicatorValue) mogen het correcte antwoord niet lekken. Een indicator die binnen 5% van het antwoord ligt, wordt geblokkeerd.',
    severity: 'BLOCKER',
    stage: 'pedagogy',
    category: 'Pedagogisch Contract',
    fix: 'Verwijder of wijzig de indicatorValue zodat deze niet het antwoord onthult.',
  },
  {
    id: 'PED-004',
    label: 'Gestructureerde data aanwezig',
    description:
      'Rekenkundige vraagtypes (cijferend_rekenen, cijferend_delen) moeten gestructureerde data bevatten in question.data in plaats van regex-parsing uit de vraagtekst.',
    severity: 'WARNING',
    stage: 'pedagogy',
    category: 'Pedagogisch Contract',
    fix: 'Voeg columnArithmetic of longDivision data toe aan het data-object van de vraag.',
  },
  // Seed Stage (5b)
  {
    id: 'SEED-001',
    label: 'Tier/skill auto-correctie',
    description:
      'De seedConsolidator heeft de tier, skill of subSkill automatisch gecorrigeerd op basis van de taxonomy-definitie. Dit duidt op een mismatch in de seed-data.',
    severity: 'WARNING',
    stage: 'seed',
    category: 'Seed-integriteit',
    fix: 'Corrigeer de tier/skill/subSkill in het seed-bestand zodat deze overeenkomen met de taxonomy.',
  },
  {
    id: 'SEED-002',
    label: 'FamilyId niet gevonden in taxonomy',
    description:
      'De familyId van de vraag komt niet voor in de taxonomy-registry.',
    severity: 'BLOCKER',
    stage: 'seed',
    category: 'Seed-integriteit',
    fix: 'Controleer de familyId en voeg deze toe aan de taxonomy als deze ontbreekt.',
  },
  // Support Stage
  {
    id: 'SUP-001',
    label: 'Uitleg geeft antwoord niet weg',
    description:
      'De uitleg mag het correcte antwoord niet direct vermelden zonder redenering.',
    severity: 'BLOCKER',
    stage: 'support',
    category: 'Ondersteuning',
    fix: 'Herschrijf de uitleg zodat deze de aanpak beschrijft, niet alleen het antwoord.',
  },
  {
    id: 'SUP-002',
    label: 'Hint aanwezig (verplichte families)',
    description:
      'Families die hintsRequired=true hebben in het beleid moeten een hint bevatten.',
    severity: 'WARNING',
    stage: 'support',
    category: 'Ondersteuning',
    fix: 'Voeg een hint toe die de aanpak uitlegt zonder het antwoord te geven.',
  },
  // Visual Stage
  {
    id: 'VIS-001',
    label: 'Stimulus visueel aanwezig (verplichte families)',
    description:
      'Families met stimulusRequired=true moeten een stimulusAids-array hebben.',
    severity: 'BLOCKER',
    stage: 'visual',
    category: 'Visueel',
    example:
      'Zie: src/governance/pipeline/defaultPolicy.ts → visual.familyRules',
    fix: 'Voeg een stimulusAids-array toe met het juiste visuele type.',
  },
  {
    id: 'VIS-002',
    label: 'Geen antwoordlek via visual',
    description:
      'Stimulus-visuals mogen het correcte antwoord niet direct zichtbaar maken via label/value.',
    severity: 'WARNING',
    stage: 'visual',
    category: 'Visueel',
    fix: 'Pas het visuele hulpmiddel aan zodat het antwoord niet direct zichtbaar is.',
  },
  {
    id: 'VIS-003',
    label: 'stimulusAids/supportAids zijn arrays',
    description:
      'Als aanwezig, moeten stimulusAids en supportAids arrays zijn (gebruik [] voor "beoordeeld, geen visuals nodig").',
    severity: 'BLOCKER',
    stage: 'visual',
    category: 'Visueel',
    fix: 'Verander het veld naar een array ([] of [{...}]).',
  },
  // Graph Stage
  {
    id: 'GRA-001',
    label: 'Geen cyclische afhankelijkheden',
    description:
      'De leerpad-graaf (LDG) mag geen cyclische afhankelijkheden bevatten.',
    severity: 'BLOCKER',
    stage: 'graph',
    category: 'LDG-Graaf',
    fix: 'Verwijder de cyclische afhankelijkheid uit de prerequisite-definitie.',
  },
  {
    id: 'GRA-002',
    label: 'FamilyId bestaat in de graaf',
    description:
      'De familyId van een vraag moet geregistreerd zijn in de LDG-graaf.',
    severity: 'WARNING',
    stage: 'graph',
    category: 'LDG-Graaf',
    fix: 'Voeg de familyId toe aan de LDG-definitie in ldgBuilder.ts.',
  },
  // Policy
  {
    id: 'POL-001',
    label: 'Minimaal aantal vragen per family',
    description: `Elke family moet minimaal ${DEFAULT_POLICY.gates.assessmentMinQuestionsPerFamily} vragen hebben voor assessment-pool.`,
    severity: 'WARNING',
    stage: 'policy',
    category: 'Beleid',
    fix: 'Voeg meer vragen toe aan de family.',
  },
  {
    id: 'POL-002',
    label: 'Kwaliteitsdrempel productie',
    description: `Vragen met score < ${DEFAULT_POLICY.gates.productionMinScore}% worden geblokkeerd voor productie.`,
    severity: 'BLOCKER',
    stage: 'policy',
    category: 'Beleid',
    fix: 'Verbeter de kwaliteit van de vraag.',
  },
  {
    id: 'POL-003',
    label: 'Kwaliteitsdrempel assessment',
    description: `Vragen met score < ${DEFAULT_POLICY.gates.assessmentMinScore}% zijn niet assessment-geschikt.`,
    severity: 'WARNING',
    stage: 'policy',
    category: 'Beleid',
    fix: 'Verbeter de kwaliteit voor assessment-gebruik.',
  },
]

// ─── Rulebook Files ───────────────────────────────────────────

export const RULEBOOK_FILES: RulebookFile[] = [
  {
    id: 'master',
    name: 'Module Authoring Guide',
    path: 'docs/guides/MODULE_AUTHORING_GUIDE.md',
    description:
      'Per-module auteursinstructies: difficulty knobs, distractor-patronen, explanation patterns, visual aids, vraagtypes.',
    module: 'ALL',
    color: 'blue',
    keyRules: [
      'familyId, module, skill, subSkill, tier, levelCode, group zijn altijd verplicht',
      'feedback.explanationText is altijd verplicht (2–6 regels, stap-voor-stap)',
      'feedback.distractorExplanations voor ELKE optie verplicht',
      'Distractors moeten realistische fouten/misconcepties vertegenwoordigen',
      'Taalgebruik moet passen bij het tier-niveau (B6 = eenvoudig, E8+ = abstract)',
      'Minimaal 1 "val-distractor" voor tiers ≥ E6',
    ],
  },
  {
    id: 'get',
    name: 'GET Rulebook',
    path: 'docs/guides/MODULE_AUTHORING_GUIDE.md#1-get--getalbegrip',
    description:
      'Aanvullende regels voor het Getalbegrip module (breuken, kommagetallen, procenten, etc.)',
    module: 'GET',
    color: 'violet',
    keyRules: [
      'LaTeX notatie verplicht voor breuken: $\\frac{1}{2}$',
      'Komma als decimaalteken (niet punt): 3,14 niet 3.14',
      'Visuele hulpmiddelen verplicht voor breuk-families (stimulusAids)',
      'Machten en wortels in LaTeX: $2^3$, $\\sqrt{9}$',
    ],
  },
  {
    id: 'met',
    name: 'MET Rulebook',
    path: 'docs/guides/MODULE_AUTHORING_GUIDE.md#3-met--meten--meetkunde',
    description:
      'Aanvullende regels voor het Metriek module (meten, oppervlakte, inhoud, omtrek)',
    module: 'MET',
    color: 'teal',
    keyRules: [
      'Eenheden altijd vermelden: cm², m², km²',
      'Formules expliciet noemen in uitleg (O = l × b)',
      'Geo-visuals verplicht voor oppervlakte/omtrek families',
      'Trap-distractor: eenheid verwisselen (cm vs. m)',
    ],
  },
  {
    id: 'tijd',
    name: 'TIJD Rulebook',
    path: 'docs/guides/MODULE_AUTHORING_GUIDE.md#4-tijd--tijd--klok',
    description:
      'Aanvullende regels voor het Tijd module (klok, kalender, tijdrekenen, snelheid)',
    module: 'TIJD',
    color: 'amber',
    keyRules: [
      'Klok-visuals verplicht voor analoge tijdvragen',
      'Tijdsduur altijd in uren EN minuten vermelden',
      'Trap-distractor: uren/minuten verwisselen',
      'Snelheid: km/u (niet km/h), formule v=s/t',
    ],
  },
  {
    id: 'geld',
    name: 'GELD Rulebook',
    path: 'docs/guides/MODULE_AUTHORING_GUIDE.md#5-geld--geld--financieel',
    description:
      'Aanvullende regels voor het Geld module (euro, wisselgeld, korting, BTW, budget)',
    module: 'GELD',
    color: 'yellow',
    keyRules: [
      'Euro-notatie: €12,50 (komma, niet punt)',
      'Money-visual verplicht voor geld-telvragen',
      'Trap-distractor: euro/cent verwisselen',
      'BTW-tarief: 21% (actueel NL)',
    ],
  },
  {
    id: 'rek',
    name: 'REK Rulebook',
    path: 'docs/guides/MODULE_AUTHORING_GUIDE.md#2-rek--basisrekenen',
    description:
      'Aanvullende regels voor Basisrekenen (tafels, cijferend, handig, context, gemengd)',
    module: 'REK',
    color: 'orange',
    keyRules: [
      'Strategienaam ALTIJD benoemen in feedback (splitsen, compenseren, rijgen)',
      'Cijferend rekenen: tussenresultaten correct, lenen bij aftrekken',
      'Contextopgaven: bewerking herkennen is de kern, niet het uitrekenen',
      'Tafels-basis: puur recall, geen contextverhalen',
    ],
  },
  {
    id: 'sww',
    name: 'SWW Rulebook',
    path: 'docs/guides/MODULE_AUTHORING_GUIDE.md#7-sww--spelling-werkwoorden',
    description:
      'Aanvullende regels voor Spelling Werkwoorden (stam, dt-regel, kofschip, etc.)',
    module: 'SWW',
    color: 'rose',
    keyRules: [
      'Kofschip-regel altijd vermelden in uitleg',
      'Stam-berekening stap voor stap uitleggen',
      'Trap-distractor: dt-fout (werkt/werkt)',
      'Hints verplicht voor alle SWW-families',
    ],
  },
  {
    id: 'spr',
    name: 'SPR Rulebook',
    path: 'docs/guides/MODULE_AUTHORING_GUIDE.md#6-spr--spelling-woordvorming',
    description:
      'Aanvullende regels voor Spelling Regels (open/gesloten lettergreep, trema, etc.)',
    module: 'SPR',
    color: 'pink',
    keyRules: [
      'Lettergreep-analyse altijd tonen in uitleg',
      'Trema-regel expliciet vermelden',
      'Trap-distractor: vergelijkbaar klinkend woord',
    ],
  },
  {
    id: 'taa',
    name: 'TAA Rulebook',
    path: 'docs/guides/MODULE_AUTHORING_GUIDE.md#8-taa--taalbeschouwing',
    description:
      'Aanvullende regels voor Taalbegrip (lezen, zinsbouw, woordsoorten)',
    module: 'TAA',
    color: 'emerald',
    keyRules: [
      'Tekst-context altijd meegeven bij leesvragen',
      'Woordsoort-definitie vermelden in uitleg',
      'Trap-distractor: vergelijkbare maar foute woordsoort',
    ],
  },
  {
    id: 'topo',
    name: 'TOPO Rulebook',
    path: 'docs/guides/MODULE_AUTHORING_GUIDE.md#10-topo--topografie',
    description:
      'Aanvullende regels voor Topografie (provincies, landen, rivieren, etc.)',
    module: 'TOPO',
    color: 'cyan',
    keyRules: [
      'Kaart-visual verplicht voor locatievragen',
      'Trap-distractor: aangrenzend land/provincie',
      'Hoofdstad vs. grootste stad onderscheid vermelden',
    ],
  },
  {
    id: 'stu',
    name: 'STU Rulebook',
    path: 'docs/guides/MODULE_AUTHORING_GUIDE.md#9-stu--studievaardigheden',
    description:
      'Aanvullende regels voor Studievaardigheden (tabellen, grafieken, kaarten)',
    module: 'STU',
    color: 'indigo',
    keyRules: [
      'Tabel/grafiek-visual altijd verplicht als stimulus',
      'Vraag verwijst altijd naar specifieke cel/punt in de visual',
      'Trap-distractor: naburige cel/waarde in de tabel',
    ],
  },
]

// ─── Pipeline Source Files ────────────────────────────────────

export const PIPELINE_FILES: PipelineFile[] = [
  {
    name: 'Schema Stage',
    path: 'src/governance/pipeline/stages/schemaStage.ts',
    stage: 'schema',
    description:
      'Valideert JSON-structuur, verplichte velden, ID-formaat en tier-bereik.',
  },
  {
    name: 'Type Stage',
    path: 'src/governance/pipeline/stages/typeStage.ts',
    stage: 'type',
    description: 'Valideert vraagtype-registratie en antwoordformaat per type.',
  },
  {
    name: 'Content Stage',
    path: 'src/governance/pipeline/stages/contentStage.ts',
    stage: 'content',
    description:
      'Valideert uitleg, distractors, taalgebruik en lengte-budgetten.',
  },
  {
    name: 'Pedagogy Stage (3b)',
    path: 'src/governance/pipeline/stages/pedagogyStage.ts',
    stage: 'pedagogy',
    description:
      'Valideert DOK-tier afstemming, misconceptie-tagging, indicator-pariteit en gestructureerde data.',
  },
  {
    name: 'Support Stage',
    path: 'src/governance/pipeline/stages/supportStage.ts',
    stage: 'support',
    description:
      'Valideert aanwezigheid van hints en uitleg voor verplichte families.',
  },
  {
    name: 'Visual Stage',
    path: 'src/governance/pipeline/stages/visualStage.ts',
    stage: 'visual',
    description:
      'Valideert visuele hulpmiddelen, antwoordlekken en array-conventie.',
  },
  {
    name: 'Graph Stage',
    path: 'src/governance/pipeline/stages/graphStage.ts',
    stage: 'graph',
    description:
      'Valideert LDG-graaf, DAG-integriteit en cyclische afhankelijkheden.',
  },
  {
    name: 'Seed Stage',
    path: 'src/governance/pipeline/stages/seedStage.ts',
    stage: 'seed',
    description:
      'Controleert seed-consolidatie waarschuwingen: tier/skill auto-correcties en taxonomy-mismatches.',
  },
  {
    name: 'Default Policy',
    path: 'src/governance/pipeline/defaultPolicy.ts',
    stage: 'policy',
    description:
      'Alle drempelwaarden, vocabulaire-lijsten, visuele regels en feature flags.',
  },
  {
    name: 'Issue Contract',
    path: 'src/governance/pipeline/issueContract.ts',
    stage: 'policy',
    description:
      'Canoniek issue-type (ValidationIssue), gate-logica en drempelwaarden.',
  },
]

// ─── Rulebook Color Map ───────────────────────────────────────

export const RULEBOOK_COLOR_MAP: Record<
  string,
  { badge: string; border: string; bg: string }
> = {
  blue: {
    badge: 'bg-blue-100 text-blue-700',
    border: 'border-blue-200',
    bg: 'bg-blue-50',
  },
  violet: {
    badge: 'bg-violet-100 text-violet-700',
    border: 'border-violet-200',
    bg: 'bg-violet-50',
  },
  teal: {
    badge: 'bg-teal-100 text-teal-700',
    border: 'border-teal-200',
    bg: 'bg-teal-50',
  },
  amber: {
    badge: 'bg-amber-100 text-amber-700',
    border: 'border-amber-200',
    bg: 'bg-amber-50',
  },
  rose: {
    badge: 'bg-rose-100 text-rose-700',
    border: 'border-rose-200',
    bg: 'bg-rose-50',
  },
  pink: {
    badge: 'bg-pink-100 text-pink-700',
    border: 'border-pink-200',
    bg: 'bg-pink-50',
  },
  emerald: {
    badge: 'bg-emerald-100 text-emerald-700',
    border: 'border-emerald-200',
    bg: 'bg-emerald-50',
  },
  cyan: {
    badge: 'bg-cyan-100 text-cyan-700',
    border: 'border-cyan-200',
    bg: 'bg-cyan-50',
  },
  indigo: {
    badge: 'bg-indigo-100 text-indigo-700',
    border: 'border-indigo-200',
    bg: 'bg-indigo-50',
  },
  orange: {
    badge: 'bg-orange-100 text-orange-700',
    border: 'border-orange-200',
    bg: 'bg-orange-50',
  },
  yellow: {
    badge: 'bg-yellow-100 text-yellow-700',
    border: 'border-yellow-200',
    bg: 'bg-yellow-50',
  },
}
